import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

// The boost/recommend response nests identifiers under result.mappings.core,
// unlike search/autocomplete which expose them at the top level. The response
// has no variant-level entries, so parentId === uid (per Searchspring guidance
// for products without variants). Exported for unit testing; this module's
// primary export is the registered custom element (a side effect).
// eslint-disable-next-line import/prefer-default-export
export function normalizeRecResult(result) {
  const core = (result && result.mappings && result.mappings.core) || {};
  return { uid: core.uid, parentId: core.uid, sku: core.sku };
}

registerComponent(
  "searchspring-recommendations",
  class SearchspringRecommendations extends WYATTElement {
    constructor() {
      super();

      this.fetchRecommendations();
    }

    get beaconEnabled() {
      return Boolean(WYATT.config.searchspring.beaconV2Enabled && WYATT.beacon);
    }

    get params() {
      const params = new URLSearchParams({
        ...WYATT.searchspring.tracking,
        beacon: true,
        limits: this.config.limits,
        tags: this.config.tags,
      });

      // Product-page placements pass the SKU on screen. Searchspring takes it
      // as the context instead of the browsing history (Customers Also Viewed)
      // and leaves it out of the results (Recently Viewed).
      if (this.config.product) {
        params.set("products", this.config.product);
      }

      // The cart drawer and cart page render their config at fetch time, so
      // these SKUs are current after an add, a quantity change or a removal.
      // tracking.cart is fixed at page load.
      if (typeof this.config.cart === "string") {
        params.set("cart", this.config.cart);
      }

      return params;
    }

    async fetchRecommendations() {
      const requestUrl = [
        "https://",
        `${WYATT.config.searchspring.siteIds.products}.`,
        `${WYATT.config.searchspring.endpoints.personalizedRecommendations}?`,
        this.params.toString(),
      ].join("");

      const response = await fetch(
        requestUrl,
        WYATT.config.searchspring.options.get,
      );

      const data = await response.json();

      this.render(data);
    }

    render(data) {
      // Response is array-wrapped, one entry per requested profile; our widgets
      // request a single tag, so data[0] is the only profile. Guard against a
      // malformed / non-array body so a bad response renders nothing rather
      // than throwing an unhandled rejection.
      const profile = (Array.isArray(data) && data[0]) || {};
      const results = profile.results || [];

      // Each profile self-identifies via profile.profile.tag; fall back to the
      // configured tag if the response omits it.
      this.tag = (profile.profile && profile.profile.tag) || this.config.tags;
      this.responseId = profile.responseId || "";

      if (this.beaconEnabled) {
        this.dataset.ssResponseId = this.responseId;
        this.dataset.ssTag = this.tag;
      }

      results.forEach(this.addProduct.bind(this));

      // A hidden, empty carousel isn't a render, so no beacon goes out for it.
      if (!results.length) {
        this.hideSection();
        return;
      }

      if (!this.beaconEnabled) return;

      WYATT.beacon.recommendations.render({
        tag: this.tag,
        responseId: this.responseId,
      });

      this.addEventListener("click", this.handleClick.bind(this));

      this.impressionObserver = WYATT.beacon.observeImpressions(
        this,
        "recommendations",
        { extraPayload: { tag: this.tag, banners: [] } },
      );
    }

    // The heading is a separate block in the same section, so an empty result
    // hides the whole section rather than leaving a heading over nothing. On a
    // first product view Recently Viewed comes back empty, since the only SKU in
    // the history is the one excluded. Scoped to this section's own
    // wrapper: the cart and search placements sit inside larger sections.
    hideSection() {
      this.closest(
        ".shopify-section--searchspring-recommendations",
      )?.classList.add("hidden");
    }

    addProduct(result) {
      const { handle } = result.attributes;
      const classList = this.config.productSummaryClassList || "";

      // product-summary reads data-handle in its constructor, which runs the
      // moment the element upgrades. Build it from markup so the attributes are
      // present before that happens: document.createElement upgrades a defined
      // element synchronously, so assigning dataset.handle afterwards bakes a
      // /products/undefined fetch into the constructor. (Mirrors results-grid.)
      const summaryMarkup = `<product-summary data-handle="${handle}" data-class-list="${classList}"></product-summary>`;

      // Without Beacon, append the bare summary as before. With Beacon, wrap it
      // in a container carrying data-ss-result: product-summary self-replaces
      // with server HTML, which would wipe per-tile state, but the wrapper (and
      // the delegated listener + impression observer on it) survives.
      if (!this.beaconEnabled) {
        this.insertAdjacentHTML("beforeend", summaryMarkup);
        return;
      }

      const tile = document.createElement("div");
      tile.dataset.ssResult = JSON.stringify(normalizeRecResult(result));
      tile.innerHTML = summaryMarkup;

      this.appendChild(tile);
    }

    handleClick(event) {
      const tile = event.target.closest("[data-ss-result]");
      if (!tile) return;

      let result;
      try {
        result = JSON.parse(tile.dataset.ssResult);
      } catch (error) {
        return;
      }

      WYATT.beacon.recommendations.clickThrough({
        tag: this.tag,
        responseId: this.responseId,
        results: [WYATT.beacon.normalizeResult(result)],
      });
    }
  },
);
